import { useQuery } from '@tanstack/react-query'
import { FilePlus2, FileText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { classesQuery } from '@/features/academic/api'
import { Can } from '@/features/auth/guards'
import { invoicesQuery, periodsQuery } from '@/features/finance/api'
import { GenerateInvoicesDialog } from '@/features/finance/components/GenerateInvoicesDialog'
import { InvoiceStatusBadge } from '@/features/finance/components/InvoiceStatusBadge'
import { invoiceStatusLabel, localizePeriodLabel, periodLabel } from '@/features/finance/status'
import { INVOICE_STATUSES, type Invoice, type InvoiceStatus } from '@/features/finance/types'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format'

const FILTERS = ['status', 'classId', 'period'] as const

export function InvoicesPage() {
  const { t } = useTranslation('finance')
  useDocumentTitle('Invoices')
  const navigate = useNavigate()
  const { money } = useMoney()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'createdAt' })
  // Newest first unless the user picked a sort direction.
  const [params] = useSearchParams()
  const list = useQuery(
    invoicesQuery({ ...query, direction: params.has('direction') ? query.direction : 'desc' }),
  )
  const classes = useQuery(classesQuery())
  const periods = useQuery(periodsQuery())
  const [generating, setGenerating] = useState(false)

  const columns: Column<Invoice>[] = [
    {
      key: 'number',
      header: t('Invoice'),
      sortKey: 'number',
      cell: (i) => (
        <div className="min-w-0">
          <Link
            to={`/admin/finance/invoices/${i.id}`}
            onClick={(e) => e.stopPropagation()}
            className="block font-mono text-xs font-medium text-ink hover:text-brand-700"
          >
            {i.number}
          </Link>
          <span className="block text-xs text-muted">{localizePeriodLabel(i.periodLabel)}</span>
        </div>
      ),
    },
    {
      key: 'student',
      header: t('Student'),
      sortKey: 'student',
      cell: (i) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-ink">{i.student.name}</span>
          <span className="block text-xs text-muted">
            {i.student.className ?? '—'} · {i.student.studentCode}
          </span>
        </div>
      ),
    },
    {
      key: 'issued',
      header: t('Issued'),
      sortKey: 'issueDate',
      hideBelow: 'xl',
      cell: (i) => formatDate(i.issueDate),
    },
    {
      key: 'due',
      header: t('Due'),
      sortKey: 'dueDate',
      hideBelow: 'lg',
      cell: (i) => (
        <span className={cn(i.isOverdue && 'font-medium text-danger-700')}>{formatDate(i.dueDate)}</span>
      ),
    },
    {
      key: 'total',
      header: t('Total'),
      sortKey: 'total',
      align: 'right',
      cell: (i) => <span className="tabular">{money(i.total)}</span>,
    },
    {
      key: 'outstanding',
      header: t('Outstanding'),
      align: 'right',
      hideBelow: 'md',
      cell: (i) => (
        <span className={cn('tabular', i.outstanding === '0.00' ? 'text-muted' : 'font-medium text-ink')}>
          {money(i.outstanding)}
        </span>
      ),
    },
    { key: 'status', header: t('Status'), cell: (i) => <InvoiceStatusBadge status={i.status} /> },
  ]

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)
  const setStatus = (s: InvoiceStatus | undefined) => update({ status: s })

  return (
    <>
      <PageHeader
        title={t('Invoices')}
        description={t('Fee invoices for every student, with live payment status.')}
        breadcrumbs={[{ label: t('Finance') }, { label: t('Invoices') }]}
        homeHref="/admin"
        actions={
          <Can permission="finance.manage">
            <Button leftIcon={<FilePlus2 />} onClick={() => setGenerating(true)}>
              {t('Generate invoices')}
            </Button>
          </Can>
        }
      />
      <div role="group" aria-label={t('Filter by status')} className="mb-4 flex flex-wrap gap-2">
        {[undefined, ...INVOICE_STATUSES].map((s) => {
          const active = (state.filters.status ?? undefined) === s
          return (
            <button
              key={s ?? 'all'}
              type="button"
              aria-pressed={active}
              onClick={() => setStatus(s)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                active
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              {s ? t(invoiceStatusLabel[s]) : t('All')}
            </button>
          )
        })}
      </div>
      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({ search: undefined, status: undefined, classId: undefined, period: undefined })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Invoice no., student or code')}
            label={t('Search invoices')}
          />
          <Select
            aria-label={t('Filter by class')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('All classes')}
            value={state.filters.classId ?? ''}
            onChange={(e) => update({ classId: e.target.value })}
            options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <Select
            aria-label={t('Filter by billing period')}
            className="sm:w-auto sm:min-w-44"
            placeholder={t('All periods')}
            value={state.filters.period ?? ''}
            onChange={(e) => update({ period: e.target.value })}
            options={(periods.data ?? []).map((p) => ({ value: p, label: periodLabel(p) }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Invoices')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(i) => i.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(i) => void navigate(`/admin/finance/invoices/${i.id}`)}
          mobileCard={(i) => (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{i.student.name}</p>
                <p className="truncate text-xs text-muted">
                  {i.number} · {localizePeriodLabel(i.periodLabel)} ·{' '}
                  {t('due {{date}}', { date: formatDate(i.dueDate) })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-ink tabular">
                  {money(i.outstanding === '0.00' ? i.total : i.outstanding)}
                </p>
                <InvoiceStatusBadge status={i.status} />
              </div>
            </div>
          )}
          empty={
            <EmptyState
              icon={<FileText />}
              title={hasFilters ? t('No invoices match these filters') : t('No invoices yet')}
              description={
                hasFilters
                  ? t('Try another status, class or period.')
                  : t('Set up fee items, then generate invoices for a billing period.')
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
      {generating ? <GenerateInvoicesDialog open onOpenChange={setGenerating} /> : null}
    </>
  )
}
